import { useEffect, useRef } from "react";

import { useOverlayOrchestrator } from "./createOverlayOrchestratorContext";
import type { OverlayType } from "./createOverlayOrchestratorContext";

/**
 * useShouldInitOverlay
 *
 * Determines whether an overlay should be mounted (initialized).
 *
 * - Returns `true` if the overlay status is `"initialized"`.
 * - Once it has been `"initialized"` at least once, the hook will
 *   always return `true`, even if the overlay status later changes
 *   to `"completed"`.
 *
 * This ensures **idempotence**:
 * - An overlay that has been mounted once will never be automatically unmounted,
 *   avoiding unnecessary remounts and preserving its internal state.
 */
export const useShouldInitOverlay = (key: OverlayType) => {
  const { state } = useOverlayOrchestrator();
  const hasInitializedRef = useRef(false);
  const status = state[key];

  useEffect(() => {
    if (status === "initialized") {
      hasInitializedRef.current = true;
    }
  }, [status]);

  return hasInitializedRef.current || status === "initialized";
};
